/**
 * Module: `api/streaming.ts` — client library utilities.
 * @remarks See exports; keep imports from concrete files when possible.
 */

import type {
  DustConversation,
  DustAction,
  DustMessageItem,
  Message,
  MessageError,
  InProgressMessage,
  ExtractedConversationData,
  MessageChainOfThought,
  MessageId,
} from "@/types";
import { extractActionType, formatActionForDisplay } from "@/lib/formatting";
import { resolveAgentMessageDisplayTimestamp } from "@/lib/ui-helper/dates";

/**
 * Process a single Dust message item into a domain Message + optional chain of thought.
 * Returns null when the item should be skipped entirely (e.g. unknown type).
 */
function processMessageItem(
  item: DustMessageItem,
  allChainOfThoughts: MessageChainOfThought[],
  lastAgentConfigRef: { value: string | null }
): Message | null {
  if (item.type === "user_message") {
    const msgId =
      item.sId ||
      `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const content = typeof item.content === "string" ? item.content : "";
    if (!content) return null;

    return {
      id: msgId as MessageId,
      role: "user",
      content,
      timestamp: item.created ? new Date(item.created) : new Date(),
    };
  }

  if (item.type === "agent_message") {
    const msgId =
      item.sId ||
      `assistant-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const createdAt = item.created ? new Date(item.created) : new Date();

    if (item.configuration?.sId) {
      lastAgentConfigRef.value = item.configuration.sId;
    }

    const content = typeof item.content === "string" ? item.content.trim() : "";

    // Determine status from the raw item (passthrough field)
    const rawItem = item as Record<string, unknown>;
    const status = rawItem.status as
      | "succeeded"
      | "failed"
      | "cancelled"
      | undefined;
    const error =
      rawItem.error && typeof rawItem.error === "object"
        ? (rawItem.error as MessageError)
        : undefined;

    // Duration of generation in ms (from Dust API; no calculation)
    const completionDurationMs =
      typeof rawItem.completionDurationMs === "number" &&
      rawItem.completionDurationMs >= 0
        ? rawItem.completionDurationMs
        : undefined;
    const completedTs =
      typeof rawItem.completedTs === "number" &&
      Number.isFinite(rawItem.completedTs)
        ? rawItem.completedTs
        : undefined;
    const displayAt = resolveAgentMessageDisplayTimestamp({
      created: createdAt,
      completedTs,
      completionDurationMs,
    });

    // Build chain of thought entry for this agent message
    const messageCoT: MessageChainOfThought = {
      messageId: msgId,
      messagePreview: content.slice(0, 80) + (content.length > 80 ? "..." : ""),
      timestamp: displayAt,
      content: "",
      actions: [],
      agentConfigurationId: item.configuration?.sId || undefined,
    };

    if (item.chainOfThought && typeof item.chainOfThought === "string") {
      messageCoT.content = item.chainOfThought;
    }

    if (item.actions && Array.isArray(item.actions)) {
      for (const action of item.actions as DustAction[]) {
        const actionId = `action-${msgId}-${action.id || Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        const actionType = extractActionType(action);
        messageCoT.actions.push({
          id: actionId,
          type: "action",
          content: formatActionForDisplay(actionType, action, {
            includeDetailSuffix: true,
          }),
          timestamp: displayAt,
        });
      }
    }

    allChainOfThoughts.push(messageCoT);

    // Failed/cancelled messages: always include so the UI can display the error state
    if (status === "failed" || status === "cancelled") {
      return {
        id: msgId as MessageId,
        role: "assistant",
        content: content || "",
        timestamp: displayAt,
        status,
        error: error || undefined,
        completionDurationMs,
      };
    }

    // In-progress (created): include so UI can show streaming placeholder; resumption will update it
    if (status === "created") {
      return {
        id: msgId as MessageId,
        role: "assistant",
        content: content || "",
        timestamp: displayAt,
        status: "created",
        completionDurationMs,
      };
    }

    // Succeeded messages stay in the transcript even when empty so the UI can
    // keep a stable question ↔ assistant turn mapping (notably for COT).
    return {
      id: msgId as MessageId,
      role: "assistant",
      content: content || "",
      timestamp: displayAt,
      status: status ?? undefined,
      completionDurationMs,
    };
  }

  return null;
}

/**
 * Extract messages and all chain of thoughts from Dust API response.
 *
 * Each element of `conversation.content` is a "turn" — an array that may
 * contain one or more messages. A typical turn has a single user or agent
 * message, but when the LLM fails and Dust retries, a turn can contain
 * multiple agent messages (e.g. failed + succeeded).
 */
export function extractMessagesFromDustResponse(
  dustResponse:
    | DustConversation
    | { conversation?: DustConversation }
    | null
    | undefined
): ExtractedConversationData {
  const messages: Message[] = [];
  const allChainOfThoughts: MessageChainOfThought[] = [];
  const lastAgentConfigRef = { value: null as string | null };
  let inProgressMessage: InProgressMessage | null = null;

  if (!dustResponse) {
    console.warn(
      "[extractMessagesFromDustResponse] No response provided, returning empty"
    );
    return {
      messages,
      allChainOfThoughts,
      lastAgentConfigurationId: null,
      inProgressMessage: null,
    };
  }

  const conversation = (
    dustResponse && "conversation" in dustResponse
      ? dustResponse.conversation
      : dustResponse
  ) as DustConversation | undefined;

  if (
    conversation &&
    conversation.content &&
    Array.isArray(conversation.content)
  ) {
    for (const turn of conversation.content) {
      // Each turn is an array of message items (usually 1, but can be >1 on retry)
      const items: DustMessageItem[] = Array.isArray(turn)
        ? (turn as DustMessageItem[])
        : [turn as DustMessageItem];

      for (const item of items) {
        if (!item) {
          console.warn(
            "[extractMessagesFromDustResponse] Skipping null item in turn:",
            turn
          );
          continue;
        }

        // Track in-progress agent message for stream resumption
        if (item.type === "agent_message") {
          const rawItem = item as Record<string, unknown>;
          const status = rawItem.status as string | undefined;
          if (status === "created") {
            const content =
              typeof item.content === "string" ? item.content : "";
            inProgressMessage = {
              sId: item.sId || "",
              snapshotContent: content,
            };
          }
        }

        const msg = processMessageItem(
          item,
          allChainOfThoughts,
          lastAgentConfigRef
        );
        if (msg) {
          messages.push(msg);
        }
      }
    }
  }

  // Sort chronologically
  messages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  // Deduplicate by id (Dust retries can emit created then succeeded for the same sId)
  const unique = new Map<string, Message>();
  for (const msg of messages) {
    const existing = unique.get(msg.id);
    if (!existing) {
      unique.set(msg.id, msg);
      continue;
    }
    const existingCreated = existing.status === "created";
    const incomingFinal = msg.status != null && msg.status !== "created";
    const preferIncoming =
      (existingCreated && incomingFinal) ||
      (msg.content?.length ?? 0) > (existing.content?.length ?? 0);
    if (preferIncoming) {
      unique.set(msg.id, msg);
    }
  }

  const finalMessages = Array.from(unique.values());
  if (inProgressMessage?.sId) {
    const hydrated = finalMessages.find(
      (message) => String(message.id) === inProgressMessage.sId
    );
    if (hydrated && hydrated.status !== "created") {
      inProgressMessage = null;
    }
  }

  return {
    messages: finalMessages,
    allChainOfThoughts,
    lastAgentConfigurationId: lastAgentConfigRef.value,
    inProgressMessage,
  };
}
